import { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type { Domain } from '@/types'
import type {
  LibraryItem,
  LibraryFilters,
  LibrarySort,
  LibraryViewMode,
  LibraryHierarchyNode,
  LibraryConnection,
  LibraryBacklink
} from '@/types/library'

interface UseLibraryOptions {
  initialFilters?: Partial<LibraryFilters>
  initialSort?: Partial<LibrarySort>
  limit?: number
}

export function useLibrary(options: UseLibraryOptions = {}) {
  const [items, setItems] = useState<LibraryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const [filters, setFilters] = useState<LibraryFilters>({
    search: '',
    types: [],
    domains: [],
    minLevel: 1,
    maxLevel: 7,
    ...options.initialFilters
  })

  const [sort, setSort] = useState<LibrarySort>({
    field: 'created_at',
    direction: 'desc',
    ...options.initialSort
  })

  const [viewMode, setViewMode] = useState<LibraryViewMode>('table')
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)

  // Fetch all library items
  const fetchItems = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setError(new Error('Supabase não configurado'))
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Fetch concepts
      const { data: concepts, error: conceptsError } = await supabase
        .from('concepts')
        .select(`
          id, name, definition, domain_id, complexity_level,
          synapse_strength, created_at, access_count, ingestion_id,
          domain:domains(id, name, color)
        `)
        .order('created_at', { ascending: false })
        .limit(options.limit || 500)

      if (conceptsError) throw conceptsError

      // Fetch principles
      const { data: principles, error: principlesError } = await supabase
        .from('principles')
        .select(`
          id, statement, complexity_level, synapse_strength,
          access_count, universality, created_at
        `)
        .order('created_at', { ascending: false })
        .limit(options.limit || 100)

      if (principlesError) throw principlesError

      // Fetch atoms
      const { data: atoms, error: atomsError } = await supabase
        .from('atoms')
        .select(`
          id, content, ingestion_id, confidence, created_at
        `)
        .order('created_at', { ascending: false })
        .limit(options.limit || 200)

      if (atomsError) throw atomsError

      // Fetch ingestions for source info
      const { data: ingestions, error: ingestionsError } = await supabase
        .from('ingestions')
        .select(`
          id, source_title, source_url, ingested_at, status,
          primary_domain:domains!ingestions_primary_domain_id_fkey(id, name, color)
        `)
        .eq('status', 'completed')
        .order('ingested_at', { ascending: false })
        .limit(options.limit || 100)

      if (ingestionsError) throw ingestionsError

      // Get connection counts
      const { data: connectionCounts } = await supabase
        .from('connections')
        .select('from_node_id, to_node_id')

      const countMap = new Map<string, number>()
      connectionCounts?.forEach(conn => {
        countMap.set(conn.from_node_id, (countMap.get(conn.from_node_id) || 0) + 1)
        countMap.set(conn.to_node_id, (countMap.get(conn.to_node_id) || 0) + 1)
      })

      // Create ingestion map for source lookup
      const ingestionMap = new Map(ingestions?.map(i => [i.id, i]) || [])

      // Transform to LibraryItems
      const libraryItems: LibraryItem[] = [
        // Concepts
        ...(concepts || []).map(c => {
          const ingestion = c.ingestion_id ? ingestionMap.get(c.ingestion_id) : null
          // Domain can be object or array depending on Supabase response
          const domainData = Array.isArray(c.domain) ? c.domain[0] : c.domain
          return {
            id: c.id,
            name: c.name,
            type: 'concept' as const,
            content: c.definition,
            domain_id: c.domain_id,
            domain: domainData as Domain | undefined,
            level: c.complexity_level,
            strength: c.synapse_strength,
            created_at: c.created_at,
            ingestion_id: c.ingestion_id,
            access_count: c.access_count,
            connection_count: countMap.get(c.id) || 0,
            source_title: ingestion?.source_title,
            source_url: ingestion?.source_url
          }
        }),
        // Principles
        ...(principles || []).map(p => ({
          id: p.id,
          name: p.statement.slice(0, 60) + (p.statement.length > 60 ? '...' : ''),
          type: 'principle' as const,
          content: p.statement,
          level: p.complexity_level,
          strength: p.synapse_strength,
          created_at: p.created_at,
          access_count: p.access_count,
          connection_count: countMap.get(p.id) || 0
        })),
        // Atoms
        ...(atoms || []).map(a => {
          const ingestion = a.ingestion_id ? ingestionMap.get(a.ingestion_id) : null
          return {
            id: a.id,
            name: a.content.slice(0, 60) + (a.content.length > 60 ? '...' : ''),
            type: 'atom' as const,
            content: a.content,
            level: 1,
            strength: a.confidence === 'high' ? 0.9 : a.confidence === 'medium' ? 0.6 : 0.3,
            created_at: a.created_at,
            ingestion_id: a.ingestion_id,
            access_count: 0,
            connection_count: countMap.get(a.id) || 0,
            source_title: ingestion?.source_title,
            source_url: ingestion?.source_url
          }
        }),
        // Ingestions as items
        ...(ingestions || []).map(i => {
          // Domain can be object or array depending on Supabase response
          const domainData = Array.isArray(i.primary_domain) ? i.primary_domain[0] : i.primary_domain
          return {
            id: i.id,
            name: i.source_title || 'Sem título',
            type: 'ingestion' as const,
            content: i.source_url || '',
            domain_id: (domainData as Domain | undefined)?.id,
            domain: domainData as Domain | undefined,
          level: 0,
          strength: 1,
          created_at: i.ingested_at,
          access_count: 0,
          connection_count: 0,
            source_title: i.source_title,
            source_url: i.source_url
          }
        })
      ]

      setItems(libraryItems)
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
    } finally {
      setLoading(false)
    }
  }, [options.limit])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  // Filtered and sorted items
  const filteredItems = useMemo(() => {
    let result = [...items]

    // Apply search filter
    if (filters.search.trim()) {
      const search = filters.search.toLowerCase()
      result = result.filter(item =>
        item.name.toLowerCase().includes(search) ||
        item.content.toLowerCase().includes(search)
      )
    }

    // Apply type filter
    if (filters.types.length > 0) {
      result = result.filter(item => filters.types.includes(item.type))
    }

    // Apply domain filter
    if (filters.domains.length > 0) {
      result = result.filter(item =>
        item.domain_id && filters.domains.includes(item.domain_id)
      )
    }

    // Apply level filter
    result = result.filter(item =>
      item.level >= filters.minLevel && item.level <= filters.maxLevel
    )

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0

      switch (sort.field) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'type':
          comparison = a.type.localeCompare(b.type)
          break
        case 'domain':
          comparison = (a.domain?.name || '').localeCompare(b.domain?.name || '')
          break
        case 'level':
          comparison = a.level - b.level
          break
        case 'strength':
          comparison = a.strength - b.strength
          break
        case 'created_at':
        default:
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
      }

      return sort.direction === 'asc' ? comparison : -comparison
    })

    return result
  }, [items, filters, sort])

  // Selected item
  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null
    return items.find(item => item.id === selectedItemId) || null
  }, [items, selectedItemId])

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<LibraryFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }, [])

  // Update sort
  const updateSort = useCallback((field: LibrarySort['field']) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }, [])

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      types: [],
      domains: [],
      minLevel: 1,
      maxLevel: 7
    })
  }, [])

  return {
    items: filteredItems,
    allItems: items,
    loading,
    error,
    filters,
    sort,
    viewMode,
    selectedItem,
    selectedItemId,
    setFilters: updateFilters,
    setSort: updateSort,
    setViewMode,
    setSelectedItemId,
    clearFilters,
    refetch: fetchItems
  }
}

// Hook for hierarchy tree data
export function useLibraryHierarchy(items: LibraryItem[], domains: Domain[]) {
  return useMemo(() => {
    const hierarchy: LibraryHierarchyNode[] = []

    // By Domain
    const byDomain: LibraryHierarchyNode = {
      id: 'by-domain',
      name: 'Por Domínio',
      type: 'domain',
      children: domains.map(domain => {
        const domainItems = items.filter(i => i.domain_id === domain.id)
        return {
          id: `domain-${domain.id}`,
          name: domain.name,
          type: 'domain' as const,
          color: domain.color,
          count: domainItems.length,
          children: domainItems.map(item => ({
            id: item.id,
            name: item.name,
            type: 'item' as const
          }))
        }
      }).filter(d => (d.count || 0) > 0)
    }

    // By Type
    const typeOrder = ['atom', 'concept', 'chain', 'model', 'principle', 'paradox', 'synthesis', 'ingestion']
    const byType: LibraryHierarchyNode = {
      id: 'by-type',
      name: 'Por Tipo NEXUS',
      type: 'node_type',
      children: typeOrder.map(type => {
        const typeItems = items.filter(i => i.type === type)
        return {
          id: `type-${type}`,
          name: type.charAt(0).toUpperCase() + type.slice(1),
          type: 'node_type' as const,
          count: typeItems.length,
          children: typeItems.slice(0, 50).map(item => ({
            id: item.id,
            name: item.name,
            type: 'item' as const
          }))
        }
      }).filter(t => (t.count || 0) > 0)
    }

    // By Ingestion
    const ingestionItems = items.filter(i => i.type === 'ingestion')
    const byIngestion: LibraryHierarchyNode = {
      id: 'by-ingestion',
      name: 'Por Fonte',
      type: 'ingestion',
      children: ingestionItems.map(ing => {
        const relatedItems = items.filter(i => i.ingestion_id === ing.id)
        return {
          id: `ingestion-${ing.id}`,
          name: ing.name,
          type: 'ingestion' as const,
          count: relatedItems.length,
          children: relatedItems.map(item => ({
            id: item.id,
            name: item.name,
            type: 'item' as const
          }))
        }
      })
    }

    hierarchy.push(byDomain, byType, byIngestion)
    return hierarchy
  }, [items, domains])
}

// Hook for item connections
export function useLibraryConnections(itemId: string | null) {
  const [connections, setConnections] = useState<LibraryConnection[]>([])
  const [backlinks, setBacklinks] = useState<LibraryBacklink[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!itemId || !isSupabaseConfigured()) {
      setConnections([])
      setBacklinks([])
      return
    }

    const fetchConnections = async () => {
      setLoading(true)
      try {
        // Get outgoing connections (simpler query without FK joins)
        const { data: outgoing, error: outError } = await supabase
          .from('connections')
          .select('id, weight, connection_type, to_node_id')
          .eq('from_node_id', itemId)
          .limit(20)

        if (outError) {
          console.warn('Error fetching outgoing connections:', outError)
        }

        // Get incoming connections (backlinks)
        const { data: incoming, error: inError } = await supabase
          .from('connections')
          .select('id, weight, connection_type, from_node_id')
          .eq('to_node_id', itemId)
          .limit(20)

        if (inError) {
          console.warn('Error fetching incoming connections:', inError)
        }

        // Get concept details for connected nodes
        const connectedIds = [
          ...(outgoing || []).map(c => c.to_node_id),
          ...(incoming || []).map(c => c.from_node_id)
        ].filter(Boolean)

        const conceptMap = new Map<string, { id: string; name: string; domain?: Domain }>()
        
        if (connectedIds.length > 0) {
          const { data: concepts } = await supabase
            .from('concepts')
            .select('id, name, domain:domains(*)')
            .in('id', connectedIds)

          concepts?.forEach(c => {
            const domainData = Array.isArray(c.domain) ? c.domain[0] : c.domain
            conceptMap.set(c.id, { 
              id: c.id, 
              name: c.name, 
              domain: domainData as Domain | undefined 
            })
          })
        }

        const formattedConnections: LibraryConnection[] = (outgoing || [])
          .map(c => {
            const target = conceptMap.get(c.to_node_id)
            return {
              id: c.id,
              from_id: itemId,
              from_name: '',
              from_type: 'concept' as const,
              to_id: c.to_node_id,
              to_name: target?.name || 'Unknown',
              to_type: 'concept' as const,
              weight: c.weight,
              connection_type: c.connection_type
            }
          })
          .sort((a, b) => b.weight - a.weight)

        const formattedBacklinks: LibraryBacklink[] = (incoming || [])
          .map(c => {
            const source = conceptMap.get(c.from_node_id)
            return {
              id: c.from_node_id,
              name: source?.name || 'Unknown',
              type: 'concept' as const,
              domain: source?.domain,
              connection_weight: c.weight
            }
          })
          .sort((a, b) => b.connection_weight - a.connection_weight)

        setConnections(formattedConnections)
        setBacklinks(formattedBacklinks)
      } catch (err) {
        console.error('Error fetching connections:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchConnections()
  }, [itemId])

  return { connections, backlinks, loading }
}
